/**
 * af-nav.js — And Friends navigation component (R04)
 *
 * Drives the cobalt bar + mega dropdown.
 *
 * Pill states (all pages, all placements): rest = plain text; hover/focus =
 * outline pill (CSS); open = inverted fill (white pill, bar-colour text).
 *
 * Open trigger (data-open-trigger):
 *  - "click" (default): first click opens the panel; clicking the open item
 *    again follows the item's own menu link; outside click / Escape closes
 *  - "hover": hover-intent opens on fine pointers (click navigates);
 *    touch behaves like click mode
 *  - sticky: fixes to the top after scrolling past its slot (works the same
 *    whether the section sits in the header group or underneath the homepage
 *    hero in the template)
 *  - sticky v2.3: listens on BOTH window and .page-wrapper — Horizon's
 *    desktop (≥990px) squeeze layout scrolls .page-wrapper, not the window,
 *    so window-only listening left docking dead on desktop. Direction is
 *    read from the anchor's own movement, so the maths never needs to know
 *    which element scrolls. data-sticky-mode="scroll-up" (new default)
 *    docks only while scrolling up and slides away scrolling down;
 *    "always" keeps the old dock-once-past behaviour. While docked, the
 *    nav sits below the stock header whenever the header's own scroll-up
 *    sticky is showing (homepage), via --af-nav-dock-top.
 *  - promo carousel: arrows + optional auto-advance (pauses on hover,
 *    respects prefers-reduced-motion)
 *  - theme editor: selecting a promo block opens its dropdown
 *  - mobile (v1.5): burger opens the bottom-sheet menu (body scroll locked,
 *    Escape / X / backdrop close); accordion items are native <details>
 *    with single-open enforcement; the sheet closes itself if the viewport
 *    grows back to desktop
 *  - menu broadcast (v2.14.2): opening/closing the mobile menus (drop
 *    band and pills band) dispatches 'af-nav:menu' ({ open }) so the
 *    collection's floating filter bar stands down while a menu is open —
 *    stacking contexts can't put the filter over the menu.
 *  - suggestions v2.14.1: input binding is DELEGATED at the document, so
 *    AF search inputs rendered anywhere (including the search results
 *    page's bar, which parses after the nav connects) feed the engine.
 *  - mobile band search (v2.14): under 750px every search entry point
 *    opens the band OVERLAY pill instead of the strip — the pill covers
 *    the band (logo included), focus lands inside the tap so the iOS
 *    keyboard opens immediately, the X / outside tap / Escape close it.
 *    The "Search for 'term'" suggestion row is gone everywhere.
 *  - mobile sticky (v2.13.3): data-sticky-mode-m overrides the sticky
 *    behaviour under 750px only ("Always pinned" vs scroll-up reveal) —
 *    desktop is untouched. Mobile shows also apply their visible state in
 *    the same tick as the dock broadcast (no double-RAF: nothing animates
 *    on mobile), so nav + filter paint in the same frame.
 *  - dock broadcast (v2.13.2): every dock show/hide dispatches an
 *    'af-nav:dock' CustomEvent on document ({ detail: { shown } }), so
 *    companion bars — the collection's floating filter controls — mirror
 *    the nav's reveal/hide in the same frame instead of running separate
 *    scroll detectors that drift apart (filter first on fast scrolls,
 *    never on slow ones).
 *  - in-bar search v2.13.1: the suggestions panel gets its own width and
 *    its right edge aligns flush with the bar's content edge — on open,
 *    the span from the pill to the end of the icon cluster is measured
 *    into --af-nav-bs-overhang.
 *  - in-bar search (v2.13): with "Search bar style: In the bar", the bar's
 *    search icon grows into a translucent pill INSIDE the bar (same tween
 *    as the homepage header field) and the suggestion panel hangs beneath
 *    it. Closed → click opens + focuses; open + empty → click closes;
 *    open + typed → the toggle is the form's submit button, so the click
 *    submits the query natively (and with JS off every click submits —
 *    landing on /search). Escape and outside clicks close. Mobile bands
 *    keep the inline strip.
 *  - search pop-up v2.9: the stock header search opener is neutralised at
 *    the source (its on:click attribute is stripped on connect and after
 *    editor section reloads) — Horizon's document-capture delegation
 *    registers before this lazy module, so racing it with listeners could
 *    never be reliable; removing the attribute is. The icon then opens
 *    the pill via our own capture listener. Typing (2+ chars) fetches
 *    Shopify predictive search (debounced, stale requests aborted) and
 *    renders word + product suggestions under the pill, per settings.
 *  - search pop-up (v2.7, desktop; v2.8 everywhere): search icons open the
 *    rounded pill search bar over a scrim (input autofocused, body scroll
 *    locked; Escape / X / scrim click close; Enter submits the form to
 *    the search page). With JS off the icons link to /search as before.
 *    v2.21.5: opening the band search now closes every mobile menu first, on
 *    every af-nav on the page — open Brands, tap search, and the menu used to
 *    stay sitting underneath the search. Only the search shows now.
 *    v2.21.4: two corrections to v2.21.3. The scroll now happens BEFORE the
 *    focus — scrolling after it fought iOS's own scroll-to-the-focused-field
 *    and the keyboard opened then dismissed itself. And cancelling listens for
 *    the user's GESTURE (touchmove / wheel) rather than 'scroll', which also
 *    fires for our own scroll-into-view and for the keyboard resizing the
 *    viewport. A gesture starting inside the overlay scrolls the suggestions
 *    and is ignored. Typing never closes it.
 *    v2.21.3: opening the band search also brings that band to the top of
 *    the viewport (instant, 8px clear) — the homepage pills band sits below
 *    the hero, so opening it from further down the page left you typing into
 *    a field that was off screen. While it is open a real scroll closes it,
 *    the same as tapping outside: armed 250ms after opening and only past a
 *    40px threshold, so neither our own scroll nor the iOS keyboard shifting
 *    the page can cancel the search it just opened.
 *    v2.21.2: that adoption is now limited to triggers that belong to NO
 *    af-nav. The listener is registered by every instance, and v2.8 assumed
 *    one nav owned the search; with three on the homepage each adopted the
 *    others' icons and whichever registered first won — so tapping the pills
 *    band opened the hero-overlay band's search at the top of the page.
 *    v2.8: a capture-phase document listener also intercepts the stock
 *    header's search action (homepage top row) and any external
 *    [data-af-nav-search-open] trigger, so every search entry point opens
 *    the one pill. On pages where the pop-up isn't rendered the stock
 *    behaviour is untouched.
 *  - mobile 2.0 (v2.5, drop band for all other pages): the burger swaps
 *    to an X in place and the menu drops down from the band as a
 *    full-height overlay (body scroll locked; Escape / X / desktop
 *    resize close). Accordion rows are the same single-open <details>.
 *  - mobile 2.0 (v2.2, homepage pills band): tapping Shop / For Friends
 *    expands the menu IN PLACE below the band (in the page flow — no body
 *    lock, no backdrop, it scrolls with the page). Tapping the open pill
 *    again, the X row or Escape closes it; tapping the other pill switches
 *    panes. Closes itself when the band docks (sticky) or the viewport
 *    grows back to desktop.
 *
 * Dependency-free; with JavaScript off the bar renders as a static in-flow
 * bar with plain links (dropdown content remains reachable via menu pages).
 */

const OPEN_DELAY = 60;
const CLOSE_DELAY = 250;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

class AfNav extends HTMLElement {
  #openTimer = null;
  #closeTimer = null;
  #activePanelId = null;
  #autoplayTimer = null;
  #spacer = null;
  #upRun = 0;
  #downRun = 0;
  #snapPending = false;
  #dockGen = 0;
  #scrollRaf = null;
  #editorHold = false;
  #mLocked = false;
  #mPrevOverflow = '';
  #m2Active = null;
  #m3Locked = false;
  #m3PrevOverflow = '';
  #searchLocked = false;
  #mSearchArmed = false;
  #mSearchArmTimer = null;
  #mSearchScrollY = 0;
  #searchPrevOverflow = '';
  #suggestTimer = null;
  #suggestAbort = null;
  #lastAnchorTop = Infinity;
  #pageWrapper = null;

  connectedCallback() {
    this.setAttribute('js-loaded', '');

    // Mobile accordion: native <details>, one open at a time.
    for (const item of this.querySelectorAll('[data-af-nav-m-acc]')) {
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        for (const other of this.querySelectorAll('[data-af-nav-m-acc][open]')) {
          if (other !== item) other.open = false;
        }
      });
    }

    this.addEventListener('click', this.#onClick);
    // Capture-phase so the pop-up wins over the stock header's own search
    // modal wiring (homepage top row) — see #onDocClick.
    document.addEventListener('click', this.#onDocClick, true);
    // The theme's event delegation registers before this module loads, so
    // interception alone can lose the race — strip the stock opener's
    // wiring instead (re-applied after editor section reloads).
    this.#stealStockSearch();
    document.addEventListener('shopify:section:load', this.#stealStockSearch);
    this.#bindSearchInputs();
    this.addEventListener('pointerenter', this.#onPointerOver, true);
    this.addEventListener('pointerleave', this.#onPointerLeave, true);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onOutsideClick);

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll);
    // Horizon's desktop (≥990px) layout scrolls .page-wrapper, not the
    // window — bind both so sticky works on every breakpoint (v2.3).
    this.#pageWrapper = document.querySelector('.page-wrapper');
    this.#pageWrapper?.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#onScroll();
    this.#publishHeight();

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onEditorBlockSelect);
      document.addEventListener('shopify:block:deselect', this.#onEditorBlockDeselect);
    }
  }

  disconnectedCallback() {
    this.#dockReset();
    this.#removeSpacer();
    document.removeEventListener('click', this.#onDocClick, true);
    document.removeEventListener('shopify:section:load', this.#stealStockSearch);
    document.removeEventListener('click', this.#onOutsideClick);
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('touchmove', this.#onMSearchGesture);
    window.removeEventListener('wheel', this.#onMSearchGesture);
    clearTimeout(this.#mSearchArmTimer);
    window.removeEventListener('resize', this.#onScroll);
    this.#pageWrapper?.removeEventListener('scroll', this.#onScroll);
    document.removeEventListener('shopify:block:select', this.#onEditorBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#onEditorBlockDeselect);
    this.#stopAutoplay();
    if (this.#mLocked) {
      document.body.style.overflow = this.#mPrevOverflow;
      this.#mLocked = false;
    }
    if (this.#m3Locked) {
      document.body.style.overflow = this.#m3PrevOverflow;
      this.#m3Locked = false;
    }
    if (this.#searchLocked) {
      document.body.style.overflow = this.#searchPrevOverflow;
      this.#searchLocked = false;
    }
  }

  /* ---------------------------------------------------- panels */

  get #triggers() {
    return [...this.querySelectorAll('[data-af-nav-trigger]')];
  }

  #panel(id) {
    return this.querySelector(`[data-af-nav-panel="${id}"]`);
  }

  #open(id) {
    if (this.#activePanelId === id) return;
    this.#close();

    const panel = this.#panel(id);
    if (!panel) return;

    panel.hidden = false;
    this.#activePanelId = id;
    this.classList.add('af-nav--open');

    for (const trigger of this.#triggers) {
      trigger.setAttribute('aria-expanded', String(trigger.dataset.afNavTrigger === id));
    }

    this.#initPromos(panel);
  }

  #close() {
    if (!this.#activePanelId) return;
    const panel = this.#panel(this.#activePanelId);
    if (panel) panel.hidden = true;

    this.#activePanelId = null;
    this.classList.remove('af-nav--open');
    for (const trigger of this.#triggers) trigger.setAttribute('aria-expanded', 'false');
    this.#stopAutoplay();
  }

  #scheduleOpen(id) {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    this.#openTimer = setTimeout(() => this.#open(id), OPEN_DELAY);
  }

  #scheduleClose() {
    if (this.#editorHold) return;
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.#close(), CLOSE_DELAY);
  }

  get #hoverOpens() {
    return this.dataset.openTrigger === 'hover' && finePointer.matches;
  }

  #onPointerOver = (event) => {
    if (!this.#hoverOpens) return;
    const trigger = event.target.closest?.('[data-af-nav-trigger]');
    if (trigger) {
      this.#scheduleOpen(trigger.dataset.afNavTrigger);
      return;
    }
    if (event.target.closest?.('.af-nav__panel')) {
      clearTimeout(this.#closeTimer);
    }
  };

  #onPointerLeave = (event) => {
    if (!this.#hoverOpens) return;
    const leaving = event.target;
    if (leaving.closest?.('[data-af-nav-trigger]') || leaving.closest?.('.af-nav__panel') || leaving === this) {
      this.#scheduleClose();
    }
  };

  #onFocusIn = (event) => {
    const trigger = event.target.closest?.('[data-af-nav-trigger]');
    if (trigger) {
      // Hover mode: focus opens (keyboard parity with hover). Click mode:
      // focus does nothing — Enter triggers the click handler instead.
      if (this.#hoverOpens) this.#open(trigger.dataset.afNavTrigger);
    } else if (!event.target.closest?.('.af-nav__panel')) {
      this.#scheduleClose();
    }
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const hdPanel = document.querySelector('[data-af-hd-search] [data-af-nav-search-results]:not([hidden])');
    if (hdPanel) {
      hdPanel.hidden = true;
      return;
    }
    const mBox = this.#mSearch;
    if (mBox && !mBox.hidden) {
      this.#mSearchClose();
      return;
    }
    const barWrap = this.#barSearch;
    if (barWrap && barWrap.classList.contains('af-nav__barsearch--open')) {
      this.#barSearchClose(barWrap);
      barWrap.querySelector('[data-af-barsearch-toggle]')?.focus();
      return;
    }
    const search = this.#search;
    if (search && !search.hidden) {
      this.#searchClose();
      this.querySelector('[data-af-nav-search-open]')?.focus();
      return;
    }
    if (this.classList.contains('af-nav--m3-open')) {
      this.#m3Close();
      this.querySelector('[data-af-nav-m3-toggle]')?.focus();
      return;
    }
    if (this.#m2Active) {
      const active = this.#m2Active;
      this.#m2Close();
      this.querySelector(`[data-af-nav-m2-pill="${active}"]`)?.focus();
      return;
    }
    const sheet = this.#mSheet;
    if (sheet && !sheet.hidden) {
      this.#mClose();
      this.querySelector('[data-af-nav-m-toggle]')?.focus();
      return;
    }
    if (this.#activePanelId) {
      const id = this.#activePanelId;
      this.#close();
      this.querySelector(`[data-af-nav-trigger="${id}"]`)?.focus();
    }
  };

  #onOutsideClick = (event) => {
    if (this.#activePanelId && !this.contains(event.target)) this.#close();
  };

  #onClick = (event) => {
    // In-bar search (v2.13): the icon grows into a pill inside the bar.
    const barToggle = event.target.closest('[data-af-barsearch-toggle]');
    if (barToggle) {
      const wrap = barToggle.closest('[data-af-nav-barsearch]');
      const input = wrap?.querySelector('.af-nav__barsearch-input');
      if (!wrap) return;
      if (!wrap.classList.contains('af-nav__barsearch--open')) {
        event.preventDefault();
        this.#barSearchOpen(wrap, input);
      } else if (!input || !input.value.trim()) {
        event.preventDefault();
        this.#barSearchClose(wrap);
      }
      // Open with a typed term: fall through — the toggle is the form's
      // submit button, so the native submit carries the query.
      return;
    }

    // Search pop-up (v2.7).
    const searchOpen = event.target.closest('[data-af-nav-search-open]');
    if (searchOpen) {
      event.preventDefault();
      this.#searchOpen();
      return;
    }
    if (event.target.closest('[data-af-nav-search-close]')) {
      this.#searchClose();
      return;
    }
    if (event.target.closest('[data-af-nav-msearch-close]')) {
      this.#mSearchClose();
      return;
    }

    // Mobile 2.0 drop band (v2.5).
    if (event.target.closest('[data-af-nav-m3-toggle]')) {
      if (this.classList.contains('af-nav--m3-open')) this.#m3Close();
      else this.#m3Open();
      return;
    }

    // Mobile 2.0 pills band (v2.2).
    const m2Pill = event.target.closest('[data-af-nav-m2-pill]');
    if (m2Pill) {
      this.#m2Toggle(m2Pill.dataset.afNavM2Pill);
      return;
    }
    if (event.target.closest('[data-af-nav-m2-close]')) {
      this.#m2Close();
      return;
    }

    // Mobile band + sheet.
    if (event.target.closest('[data-af-nav-m-toggle]')) {
      this.#mOpen();
      return;
    }
    if (event.target.closest('[data-af-nav-m-close]')) {
      this.#mClose();
      return;
    }

    // Click mode (and any touch): first click/tap opens; clicking the open
    // item again falls through and follows the item's own menu link.
    const trigger = event.target.closest('[data-af-nav-trigger]');
    if (trigger && !this.#hoverOpens) {
      const id = trigger.dataset.afNavTrigger;
      if (this.#activePanelId !== id) {
        event.preventDefault();
        this.#open(id);
      }
    }
  };

  /* ---------------------------------------------------- mobile sheet */

  get #mSheet() {
    return this.querySelector('[data-af-nav-m-sheet]');
  }

  #mOpen() {
    const sheet = this.#mSheet;
    if (!sheet || !sheet.hidden) return;
    sheet.hidden = false;
    requestAnimationFrame(() => sheet.classList.add('is-open'));
    this.querySelector('[data-af-nav-m-toggle]')?.setAttribute('aria-expanded', 'true');
    this.#mPrevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.#mLocked = true;
  }

  #mClose() {
    const sheet = this.#mSheet;
    if (!sheet || sheet.hidden) return;
    sheet.classList.remove('is-open');
    this.querySelector('[data-af-nav-m-toggle]')?.setAttribute('aria-expanded', 'false');
    if (this.#mLocked) {
      document.body.style.overflow = this.#mPrevOverflow;
      this.#mLocked = false;
    }
    const finish = () => {
      sheet.hidden = true;
    };
    if (reducedMotion.matches) finish();
    else setTimeout(finish, 280);
  }

  /* ---------------------------------------- search pop-up (v2.7) */

  get #search() {
    return this.querySelector('[data-af-nav-search]');
  }

  // The stock header's search button opens the theme's own modal via a
  // delegated on:click attribute. Removing the attribute makes that
  // wiring inert — deterministically, no listener races — while our
  // capture listener below gives the icon the pill instead.
  #stealStockSearch = () => {
    // Re-runs after editor section reloads — also rebinds any AF search
    // fields the reload replaced (the header's always-visible field, v2.12).
    this.#bindSearchInputs();
    if (!this.#search) return;
    for (const el of document.querySelectorAll('.search-action [on\\:click]')) {
      el.removeAttribute('on:click');
      el.setAttribute('data-af-search-stolen', '');
    }
  };

  // Every AF search input on the page shares the one suggestion engine.
  // v2.14.1: DELEGATED at the document — per-input binding ran at nav
  // connect, which happens while the page is still parsing, so inputs
  // rendered later in the template (the search results page's bar) were
  // never bound. One capture listener catches them all, whenever they
  // appear. Window-flag so a second nav copy doesn't double-fetch.
  #bindSearchInputs = () => {
    if (window.__afNavSuggestDelegated) return;
    window.__afNavSuggestDelegated = true;
    document.addEventListener(
      'input',
      (event) => {
        if (event.target?.classList?.contains('af-nav__search-input')) this.#onSearchInput(event);
      },
      true
    );
  };

  /* ------------------------------------- search suggestions (v2.9) */

  #onSearchInput = (event) => {
    const cfg = event.target.closest('[data-suggest]');
    if (!cfg) return;
    const panel = cfg.querySelector('[data-af-nav-search-results]');
    if (!panel) return;
    const term = event.target.value.trim();
    clearTimeout(this.#suggestTimer);
    if (term.length < 2) {
      this.#renderSuggestions(null, '', cfg, panel);
      return;
    }
    this.#suggestTimer = setTimeout(() => this.#fetchSuggestions(term, cfg, panel), 220);
  };

  async #fetchSuggestions(term, cfg, panel) {
    if (!cfg || !panel) return;
    this.#suggestAbort?.abort();
    this.#suggestAbort = new AbortController();
    const limit = parseInt(cfg.dataset.suggestLimit || '4', 10);
    const wantQ = cfg.dataset.suggestQueries !== 'false';
    const wantP = cfg.dataset.suggestProducts !== 'false';
    const types = [wantQ ? 'query' : null, wantP ? 'product' : null].filter(Boolean).join(',');
    if (!types) return;
    const root = window.Shopify?.routes?.root || '/';
    const url =
      `${root}search/suggest.json?q=${encodeURIComponent(term)}` +
      `&resources[type]=${types}&resources[limit]=${limit}&resources[limit_scope]=each` +
      `&resources[options][unavailable_products]=last`;
    try {
      const res = await fetch(url, { signal: this.#suggestAbort.signal });
      if (!res.ok) return;
      const data = await res.json();
      this.#renderSuggestions(data.resources?.results || null, term, cfg, panel);
    } catch (err) {
      if (err?.name !== 'AbortError') this.#renderSuggestions(null, '', cfg, panel);
    }
  }

  #renderSuggestions(results, term = '', cfg = null, panel = null) {
    cfg = cfg || this.#search;
    panel = panel || cfg?.querySelector('[data-af-nav-search-results]');
    if (!panel) return;
    panel.textContent = '';
    const queries = results?.queries || [];
    const products = results?.products || [];
    if (!queries.length && !products.length && !term) {
      panel.hidden = true;
      return;
    }
    const currency = cfg?.dataset.currency || '';
    for (const q of queries) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'af-nav__suggest-q';
      btn.innerHTML =
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="6.2"/><path d="M15.6 15.6 20 20"/></svg>';
      const span = document.createElement('span');
      span.textContent = q.text;
      btn.append(span);
      btn.addEventListener('click', () => {
        const input = cfg?.querySelector('.af-nav__search-input');
        const form = cfg?.querySelector('form');
        if (input) input.value = q.text;
        form?.requestSubmit();
      });
      panel.append(btn);
    }
    if (queries.length && products.length) {
      const rule = document.createElement('div');
      rule.className = 'af-nav__suggest-rule';
      rule.setAttribute('aria-hidden', 'true');
      panel.append(rule);
    }
    for (const p of products) {
      const a = document.createElement('a');
      a.className = 'af-nav__suggest-p';
      a.href = p.url;
      const img = document.createElement('img');
      img.alt = '';
      img.loading = 'lazy';
      if (p.featured_image?.url) img.src = `${p.featured_image.url}&width=96`;
      else if (p.image) img.src = p.image;
      const title = document.createElement('span');
      title.className = 'af-nav__suggest-p-title';
      title.textContent = p.title;
      const price = document.createElement('span');
      price.className = 'af-nav__suggest-p-price';
      price.textContent = p.price ? `${currency}${p.price}` : '';
      a.append(img, title, price);
      panel.append(a);
    }
    // v2.14: the "Search for 'term'" row is gone — the word suggestions
    // above already cover it, and Enter submits the form.
    panel.hidden = false;
  }

  // Document-level (capture): opens the pill from triggers OUTSIDE this
  // component — most importantly the stock header's search icon on the
  // homepage — before the theme's own modal wiring can react. Inert when
  // this nav has no pop-up rendered.
  #onDocClick = (event) => {
    // The header's always-visible field (v2.12): clicking anywhere outside
    // it puts its suggestion panel away.
    if (!event.target.closest('[data-af-hd-search]')) {
      for (const p of document.querySelectorAll('[data-af-hd-search] [data-af-nav-search-results]')) {
        p.hidden = true;
      }
    }
    // In-bar search (v2.13): clicking anywhere outside it closes the pill.
    if (!event.target.closest('[data-af-nav-barsearch]')) this.#barSearchClose();
    // Mobile band overlay (v2.14): same outside-tap rule. (On the opening
    // tap this capture handler runs while the overlay is still hidden, so
    // the close is a no-op — only later outside taps close it.)
    if (!event.target.closest('[data-af-nav-msearch]') && !event.target.closest('[data-af-nav-search-open]')) this.#mSearchClose();
    if (!this.#search) return;
    const external = event.target.closest('[data-af-nav-search-open]');
    if (external && !this.contains(external)) {
      /* v2.21.2: only adopt a trigger that belongs to NO af-nav.
         This capture-phase listener is registered by EVERY af-nav instance,
         and v2.8 wrote it assuming one nav owned the search ("so every search
         entry point opens the one pill"). The homepage runs three: tapping the
         pills band's icon was adopted by the hero-overlay copy, which called
         stopPropagation — so the tapped nav never saw its own click — and then
         opened ITS band overlay, pinned to the top of the page. A trigger
         inside another af-nav is that nav's to handle; leave it alone and let
         the event reach it. The genuinely external case this block exists for
         — the stock header's search action — is matched separately below and
         is unaffected. */
      if (external.closest('af-nav')) return;
      event.preventDefault();
      event.stopPropagation();
      this.#searchOpen(true);
      return;
    }
    const stockSearch = event.target.closest('.header-section .search-action, #header-component .search-action');
    if (stockSearch) {
      event.preventDefault();
      event.stopPropagation();
      this.#searchOpen(true);
    }
  };

  /* ------------------------------------- in-bar search (v2.13) */

  get #barSearch() {
    return this.querySelector('[data-af-nav-barsearch]');
  }

  #barSearchOpen(wrap, input) {
    // v2.13.1: measure the span from the pill's right edge to the end of
    // the icon cluster, so the suggestions panel's right edge sits flush
    // with the bar's content edge (the cart icon / page-margin line).
    const cluster = wrap.closest('.af-nav__utility');
    if (cluster) {
      const over = Math.max(0, Math.round(cluster.getBoundingClientRect().right - wrap.getBoundingClientRect().right));
      wrap.style.setProperty('--af-nav-bs-overhang', `${over}px`);
    }
    wrap.classList.add('af-nav__barsearch--open');
    wrap.querySelector('[data-af-barsearch-toggle]')?.setAttribute('aria-expanded', 'true');
    input?.removeAttribute('tabindex');
    // Focus once the pill has begun to open so the caret lands in view.
    setTimeout(() => input?.focus(), 130);
  }

  #barSearchClose(wrap) {
    wrap = wrap || this.#barSearch;
    if (!wrap || !wrap.classList.contains('af-nav__barsearch--open')) return;
    wrap.classList.remove('af-nav__barsearch--open');
    wrap.querySelector('[data-af-barsearch-toggle]')?.setAttribute('aria-expanded', 'false');
    const input = wrap.querySelector('.af-nav__barsearch-input');
    if (input) {
      input.setAttribute('tabindex', '-1');
      input.blur();
    }
    const panel = wrap.querySelector('[data-af-nav-search-results]');
    if (panel) panel.hidden = true;
  }

  /* ------------------------------------- mobile band search (v2.14) */

  get #mSearch() {
    return this.querySelector('[data-af-nav-msearch]');
  }

  /* v2.21.4: listen for the USER'S scroll GESTURE, not for 'scroll'.
     'scroll' fires for our own scroll-into-view and again when the iOS
     keyboard resizes the viewport, so it could never tell "the customer
     scrolled away" from "the search just opened". touchmove and wheel only
     happen when a person does them. A gesture starting INSIDE the overlay is
     scrolling the suggestions list, not leaving, so it is ignored. */
  #onMSearchGesture = (event) => {
    if (!this.#mSearchArmed) return;
    if (event.target?.closest?.('[data-af-nav-msearch]')) return;
    this.#mSearchClose();
  };

  /* Public so one nav can quiet the others — the homepage stacks two bands and
     the menu you have open may not belong to the band you tapped search on.
     Each closer already no-ops when its own menu is shut. */
  closeMobileMenus() {
    this.#mClose();
    this.#m2Close();
    this.#m3Close();
  }

  #mSearchOpen() {
    const box = this.#mSearch;
    if (!box || !box.hidden) return;

    /* v2.21.5: opening the search over an open menu left both on screen —
       tap Brands, then tap search, and the menu was still sitting underneath.
       Close every mobile menu on every nav first, so the search is the only
       thing showing. The band itself sits ABOVE the menu it opens, so this
       does not move the band and the scroll target below stays correct. */
    document.querySelectorAll('af-nav').forEach((nav) => {
      if (typeof nav.closeMobileMenus === 'function') nav.closeMobileMenus();
    });

    box.hidden = false;

    /* v2.21.4: SCROLL FIRST, THEN FOCUS — both still inside the tap gesture.
       Scrolling AFTER the focus fights iOS's own scroll-to-the-focused-field
       behaviour, and the keyboard opens then dismisses itself immediately.
       Scrolling first leaves iOS a stable position to focus into, so the
       keyboard opens once and stays up to be typed into. */
    const bar = box.closest('.af-nav__m-bar') || box;
    const rect = bar.getBoundingClientRect();
    // A docked (sticky) band already has rect.top ~0, so this resolves to the
    // current position and nothing moves.
    const target = Math.max(0, window.scrollY + rect.top - 8);
    if (Math.abs(target - window.scrollY) > 2) window.scrollTo(0, target);

    // Focus INSIDE the tap gesture — that's what makes iOS open the
    // keyboard without a second tap. No RAF, no timeout.
    box.querySelector('.af-nav__msearch-input')?.focus({ preventScroll: true });

    /* A short grace so a sloppy tap that drags a few pixels does not read as
       "scrolled away" the instant it opens. */
    this.#mSearchArmed = false;
    clearTimeout(this.#mSearchArmTimer);
    this.#mSearchArmTimer = setTimeout(() => { this.#mSearchArmed = true; }, 300);
    window.addEventListener('touchmove', this.#onMSearchGesture, { passive: true });
    window.addEventListener('wheel', this.#onMSearchGesture, { passive: true });
  }

  #mSearchClose() {
    const box = this.#mSearch;
    if (!box || box.hidden) return;
    clearTimeout(this.#suggestTimer);
    this.#suggestAbort?.abort();
    clearTimeout(this.#mSearchArmTimer);
    this.#mSearchArmed = false;
    window.removeEventListener('touchmove', this.#onMSearchGesture);
    window.removeEventListener('wheel', this.#onMSearchGesture);
    const panel = box.querySelector('[data-af-nav-search-results]');
    if (panel) panel.hidden = true;
    box.querySelector('.af-nav__msearch-input')?.blur();
    box.hidden = true;
  }

  #searchOpen(fromTop = false) {
    // v2.14: on mobile the band overlay IS the search — every entry point
    // routes there (called synchronously from the tap, so focus counts).
    if (window.innerWidth < 750 && this.#mSearch) {
      this.#mSearchOpen();
      return;
    }
    const search = this.#search;
    if (!search || !search.hidden) return;
    // Triggered from the page top (the stock header's icon, homepage): the
    // inline strip drops from under the header instead of from the af-nav
    // copy that may sit below the hero (v2.11.1). Style "bar" keeps the
    // inline strip for these external/mobile entry points (v2.13).
    if (fromTop && search.dataset.searchStyle !== 'popup') {
      const header = document.querySelector('#header-component, .header-section .header');
      const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      search.style.setProperty('--af-nav-search-top-fix', `${top}px`);
      search.classList.add('af-nav__search--from-top');
    }
    search.hidden = false;
    requestAnimationFrame(() => {
      search.classList.add('is-open');
      search.querySelector('.af-nav__search-input')?.focus();
    });
    // The inline bar (v2.11) sits in the page like the dropdown — no lock.
    // Only the pop-up pill locks scroll ("bar" style reuses the strip).
    if (search.dataset.searchStyle === 'popup') {
      this.#searchPrevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.#searchLocked = true;
    }
  }

  #searchClose() {
    const search = this.#search;
    if (!search || search.hidden) return;
    clearTimeout(this.#suggestTimer);
    this.#suggestAbort?.abort();
    this.#renderSuggestions(null);
    search.classList.remove('is-open');
    search.classList.remove('af-nav__search--from-top');
    if (this.#searchLocked) {
      document.body.style.overflow = this.#searchPrevOverflow;
      this.#searchLocked = false;
    }
    const finish = () => {
      search.hidden = true;
    };
    if (reducedMotion.matches) finish();
    else setTimeout(finish, 200);
  }

  /* -------------------------------------- mobile 2.0 drop band (v2.5) */

  get #m3() {
    return this.querySelector('[data-af-nav-m3]');
  }

  #m3Open() {
    const m3 = this.#m3;
    if (!m3 || !m3.hidden) return;
    // The overlay starts exactly under the band, wherever it currently is
    // (in flow at the top, or docked by sticky).
    const bar = this.#visibleBar();
    const top = bar ? Math.max(0, Math.round(bar.getBoundingClientRect().bottom)) : 0;
    this.style.setProperty('--af-nav-m3-top', `${top}px`);
    m3.hidden = false;
    requestAnimationFrame(() => m3.classList.add('is-open'));
    this.classList.add('af-nav--m3-open');
    this.querySelector('[data-af-nav-m3-toggle]')?.setAttribute('aria-expanded', 'true');
    this.#m3PrevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.#m3Locked = true;
    this.#menuEmit(true);
  }

  #m3Close() {
    const m3 = this.#m3;
    if (!m3 || m3.hidden) return;
    this.#menuEmit(false);
    m3.classList.remove('is-open');
    this.classList.remove('af-nav--m3-open');
    this.querySelector('[data-af-nav-m3-toggle]')?.setAttribute('aria-expanded', 'false');
    if (this.#m3Locked) {
      document.body.style.overflow = this.#m3PrevOverflow;
      this.#m3Locked = false;
    }
    const finish = () => {
      m3.hidden = true;
    };
    if (reducedMotion.matches) finish();
    else setTimeout(finish, 200);
  }

  /* ------------------------------------------- mobile 2.0 pills (v2.2) */

  get #m2() {
    return this.querySelector('[data-af-nav-m2]');
  }

  // Tapping a pill opens its pane in place (the wrapper grows in the page
  // flow); tapping the open pill again closes; tapping the other switches.
  #m2Toggle(which) {
    const m2 = this.#m2;
    if (!m2 || !which) return;
    if (this.#m2Active === which) {
      this.#m2Close();
      return;
    }
    this.#m2Active = which;
    for (const pane of m2.querySelectorAll('[data-af-nav-m2-pane]')) {
      pane.hidden = pane.dataset.afNavM2Pane !== which;
    }
    for (const pill of this.querySelectorAll('[data-af-nav-m2-pill]')) {
      const on = pill.dataset.afNavM2Pill === which;
      pill.classList.toggle('is-active', on);
      pill.setAttribute('aria-expanded', on ? 'true' : 'false');
    }
    m2.classList.add('is-open');
    this.#menuEmit(true);
  }

  #m2Close() {
    const m2 = this.#m2;
    if (!m2 || !this.#m2Active) return;
    this.#menuEmit(false);
    this.#m2Active = null;
    m2.classList.remove('is-open');
    for (const pill of this.querySelectorAll('[data-af-nav-m2-pill]')) {
      pill.classList.remove('is-active');
      pill.setAttribute('aria-expanded', 'false');
    }
  }

  /* ---------------------------------------------------- sticky */

  // Whichever bar is currently rendered (desktop bar ≥750px, mobile band
  // below) is the one whose height matters.
  #visibleBar() {
    return [...this.querySelectorAll('.af-nav__bar, .af-nav__m-bar')].find((bar) => bar.offsetHeight > 0);
  }

  #publishHeight() {
    // The stock header owns --header-group-height wherever it renders (the
    // homepage: transparent over the hero, which pulls up by this value).
    // Publishing the af-nav bar height there clobbers the stock value and
    // opens a white gap above the hero. So af-nav only publishes when it IS
    // the page's header — i.e. the stock header rendered nothing (R07).
    if (document.querySelector('.header-section .header, #header-component')) return;
    const bar = this.#visibleBar();
    if (bar) {
      document.body.style.setProperty('--header-group-height', `${bar.offsetHeight}px`);
    }
  }

  #onScroll = () => {
    if (this.#scrollRaf) return;
    this.#scrollRaf = requestAnimationFrame(() => {
      this.#scrollRaf = null;
      if (window.innerWidth >= 750) {
        this.#mClose();
        this.#m2Close();
        this.#m3Close();
      }
      const barHeight = this.#visibleBar()?.offsetHeight || 0;
      this.#applySticky(barHeight);
    });
  };

  #applySticky(barHeight) {
    if (!this.hasAttribute('data-sticky')) return;

    // The theme editor jumps scroll positions constantly (section selects,
    // reloads), which makes docking fire spuriously and overlap the header
    // in preview (v2.12.1). Preview shows the nav in flow; the storefront
    // keeps the full sticky behaviour.
    if (window.Shopify?.designMode) {
      if (this.classList.contains('af-nav--docked-top')) {
        this.#dockReset();
        this.#removeSpacer();
        this.style.removeProperty('--af-nav-dock-top');
      }
      return;
    }

    const wrapper = this.closest('.shopify-section') || this;
    const anchorTop = this.#spacer
      ? this.#spacer.getBoundingClientRect().top
      : wrapper.getBoundingClientRect().top;

    // Scroll direction from the anchor's own movement — breakpoint-proof,
    // since it never matters which element (window or .page-wrapper) scrolls.
    // v2.12.5: slow trackpad/wheel scrolling jitters by a pixel either way,
    // which read as direction flips and made the dock animation fight itself
    // (in–out–in = snappy). Sub-2px deltas are ignored outright — the anchor
    // only advances on real movement, so jitter can't flip the direction.
    const delta = anchorTop - this.#lastAnchorTop;
    const moved = Math.abs(delta) >= 2;
    const scrollingUp = delta > 0;
    if (moved) this.#lastAnchorTop = anchorTop;

    // While the in-flow 2.0 menu is open, hold off docking — the user may be
    // scrolling down through a long pane (e.g. Brands); snapping the whole
    // nav to the top would yank it away mid-read. Sticky resumes on close.
    if (this.#m2Active && !this.classList.contains('af-nav--docked-top')) return;

    const docked = this.classList.contains('af-nav--docked-top');
    // v2.13.3: mobile can run its own behaviour ("Always pinned") while
    // desktop keeps scroll-up — the setting only overrides under 750px.
    let mode = this.getAttribute('data-sticky-mode') || 'always';
    if (window.innerWidth < 750) {
      mode = this.getAttribute('data-sticky-mode-m') || mode;
    }

    // Back at (or above) the natural slot: fully reset. >= with a 1px
    // tolerance (v2.12.2): on pages where the nav is the FIRST section its
    // slot sits exactly at scroll 0, and the old `> 0` could never fire
    // there — an up-scroll dock at the top then one click down released the
    // nav but left the spacer in flow, showing as a white bar above the bar.
    if (anchorTop >= -1) {
      if (docked) this.#dockReset();
      this.#removeSpacer();
      this.style.removeProperty('--af-nav-dock-top');
      return;
    }

    // Past the slot (v2.12.6): the bar stays FIXED the whole time and only
    // its transform toggles between hidden-above and shown — zero layout
    // changes in the scroll path, so there is nothing left to stutter.
    // Reveal after a small upward run; tuck away after a deliberate
    // downward run. A wrong flip is just a transform transition, which
    // reverses smoothly mid-flight.
    if (!docked) {
      // Swap to fixed only once the bar has FULLY scrolled off the top —
      // switching earlier would vanish a still-half-visible bar. Until
      // then it stays in flow and scrolls naturally.
      if (anchorTop < -(barHeight + 8)) {
        this.#addSpacer(barHeight);
        this.#upRun = 0;
        this.#downRun = 0;
        if (mode === 'scroll-up') {
          this.#dockArriveHidden();
        } else {
          this.#dockShow();
        }
      }
    } else if (anchorTop > -(barHeight + 8)) {
      // Boundary zone (v2.12.7): the slot is partially back on screen, so a
      // hidden or mid-slide bar would show the spacer as a white gap at the
      // top — here the bar is always PRESENT, instantly (no transition), and
      // the -1 reset swaps it seamlessly back into flow.
      this.#dockSnapIn();
    } else if (mode === 'scroll-up' && moved) {
      if (scrollingUp) {
        this.#downRun = 0;
        this.#upRun += delta;
        if (this.#upRun >= 6) this.#dockShow();
      } else {
        this.#upRun = 0;
        this.#downRun += -delta;
        if (this.#downRun >= 30) this.#dockHide();
      }
    }

    if (this.classList.contains('af-nav--docked-top')) this.#updateDockTop();
  }

  // On the homepage the stock header can be showing its own scroll-up
  // sticky at the same moment — sit the docked nav just below it so the
  // two stack (top row above, cobalt band beneath) instead of overlapping.
  #updateDockTop() {
    const stuck = document.querySelector(
      '#header-component[data-sticky-state="active"], header-component[data-sticky-state="active"]'
    );
    const top = stuck ? Math.max(0, Math.round(stuck.getBoundingClientRect().bottom)) : 0;
    if (top > 0) this.style.setProperty('--af-nav-dock-top', `${top}px`);
    else this.style.removeProperty('--af-nav-dock-top');
    this.#updateDockWidth();
  }

  // Fixed positioning spans the viewport, but the page scrolls inside
  // .page-wrapper whose content stops before its scrollbar — dock at the
  // scroller's CONTENT width so the bar doesn't shift sideways (v2.12.3).
  #updateDockWidth() {
    const pw = document.querySelector('.page-wrapper');
    const scroller =
      pw && getComputedStyle(pw).overflowY === 'auto' ? pw : document.documentElement;
    const w = scroller.clientWidth;
    if (w && Math.abs(w - window.innerWidth) > 0.5) {
      this.style.setProperty('--af-nav-dock-w', `${w}px`);
    } else {
      this.style.removeProperty('--af-nav-dock-w');
    }
  }

  /* Dock choreography (v2.12.6): past the slot the bar is always fixed;
     .af-nav--dock-in slides it in, removing it slides it out. Show is
     idempotent — the double-RAF only matters on the tick that also adds
     the fixed class, so the hidden start state paints first. */

  // v2.13.2: every dock state change is broadcast so companion bars (the
  // collection's floating filter controls) can mirror it in the SAME frame
  // instead of running their own out-of-sync scroll detectors.
  #dockEmit(shown) {
    document.dispatchEvent(new CustomEvent('af-nav:dock', { detail: { shown } }));
  }

  // v2.14.2: companion bars stand down while a mobile menu is open.
  #menuEmit(open) {
    document.dispatchEvent(new CustomEvent('af-nav:menu', { detail: { open } }));
  }

  #dockShow() {
    this.classList.add('af-nav--docked-top');
    // v2.13.3: mobile has no tween (the v2.16.1 CSS) — apply the visible
    // state in the SAME tick as the broadcast, so the nav and the filter
    // bar flip together in one paint. The double-RAF below exists only to
    // let desktop's slide animation start from the hidden state, and it
    // made the mobile nav trail the filter by up to two frames.
    if (window.innerWidth < 750) {
      this.#dockGen++;
      this.classList.add('af-nav--dock-in');
      this.#dockEmit(true);
      return;
    }
    this.#dockEmit(true);
    if (this.classList.contains('af-nav--dock-in')) return;
    // Generation token (v2.12.8): the show lands two frames later — if a
    // hide/reset/snap happens in between, the stale show must NOT apply
    // (it made the bar flash in and animate straight back out on jittery
    // down-scrolls near the top).
    const gen = ++this.#dockGen;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (gen === this.#dockGen && this.classList.contains('af-nav--docked-top')) {
          this.classList.add('af-nav--dock-in');
        }
      });
    });
  }

  #dockHide() {
    this.#dockGen++;
    this.classList.remove('af-nav--dock-in');
    this.#dockEmit(false);
  }

  // Enter the fixed state ALREADY hidden, with the transition suppressed
  // for that frame (v2.12.9) — otherwise the transform TRANSITIONS from
  // the in-flow value to hidden, which painted the bar at the top for a
  // beat and slid it away: the down-scroll flicker at the moment the bar
  // finishes scrolling off.
  #dockArriveHidden() {
    this.#dockGen++;
    this.style.transition = 'none';
    this.classList.remove('af-nav--dock-in');
    this.classList.add('af-nav--docked-top');
    this.#dockEmit(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.style.removeProperty('transition');
      });
    });
  }

  // Show with NO animation — used in the boundary zone where the slide
  // itself would flash the spacer white behind the moving bar.
  #dockSnapIn() {
    if (!this.classList.contains('af-nav--docked-top')) return;
    this.#dockGen++;
    if (this.classList.contains('af-nav--dock-in') && !this.#snapPending) {
      // May still be mid-transition from an animated show — pin it.
      const t = getComputedStyle(this).transform;
      if (t === 'none' || t === 'matrix(1, 0, 0, 1, 0, 0)') return;
    }
    this.#snapPending = true;
    this.style.transition = 'none';
    this.classList.add('af-nav--dock-in');
    this.#dockEmit(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.style.removeProperty('transition');
        this.#snapPending = false;
      });
    });
  }

  #dockReset() {
    this.#dockGen++;
    this.classList.remove('af-nav--docked-top', 'af-nav--dock-in');
    this.#upRun = 0;
    this.#downRun = 0;
    this.#dockEmit(false);
  }

  #addSpacer(height) {
    if (this.#spacer) return;
    this.#spacer = document.createElement('div');
    this.#spacer.style.height = `${height}px`;
    this.#spacer.setAttribute('aria-hidden', 'true');
    this.parentNode?.insertBefore(this.#spacer, this);
  }

  #removeSpacer() {
    this.#spacer?.remove();
    this.#spacer = null;
  }

  /* ---------------------------------------------------- promo carousel */

  #initPromos(panel) {
    const promos = panel.querySelector('[data-af-nav-promos]');
    if (!promos) return;

    // Dots reflect position only (swipe or auto-advance moves the cards).
    const track = promos.querySelector('[data-af-nav-promo-track]');
    if (track && !track.dataset.afDotsBound) {
      track.dataset.afDotsBound = '1';
      track.addEventListener('scroll', () => this.#syncDots(promos), { passive: true });
    }
    this.#syncDots(promos);
    this.#startAutoplay(panel);
  }

  #syncDots(promos) {
    const track = promos.querySelector('[data-af-nav-promo-track]');
    const dots = promos.querySelectorAll('.af-nav__promo-dot');
    if (!track || !dots.length) return;
    const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  }

  #slidePromos(promos, direction) {
    const track = promos?.querySelector('[data-af-nav-promo-track]');
    if (!track) return;
    const width = track.clientWidth;
    const atEnd = track.scrollLeft + width >= track.scrollWidth - 4;
    const atStart = track.scrollLeft <= 4;

    let left;
    if (direction > 0) {
      left = atEnd ? 0 : track.scrollLeft + width;
    } else {
      left = atStart ? track.scrollWidth : track.scrollLeft - width;
    }
    track.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  #startAutoplay(panel) {
    this.#stopAutoplay();
    const seconds = Number(this.dataset.autoplay || 0);
    if (!seconds || reducedMotion.matches) return;

    const promos = panel.querySelector('.af-nav__promos--carousel');
    if (!promos) return;

    this.#autoplayTimer = setInterval(() => {
      if (promos.matches(':hover') || promos.contains(document.activeElement)) return;
      this.#slidePromos(promos, 1);
    }, seconds * 1000);
  }

  #stopAutoplay() {
    clearInterval(this.#autoplayTimer);
    this.#autoplayTimer = null;
  }

  /* ---------------------------------------------------- theme editor */

  #onEditorBlockSelect = (event) => {
    const panel = event.target.closest?.('.af-nav__panel');
    if (!panel || !this.contains(panel)) return;
    this.#editorHold = true;
    this.#open(panel.dataset.afNavPanel);
  };

  #onEditorBlockDeselect = (event) => {
    if (!this.contains(event.target)) return;
    this.#editorHold = false;
    this.#close();
  };
}

if (!customElements.get('af-nav')) {
  customElements.define('af-nav', AfNav);
}